import React, { useMemo, useState } from 'react';
import { HeroSection } from '../../components/HeroSection';
import { Search, BookOpen, Filter } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface Term {
  word: string;
  category: 'core-concepts' | 'systems' | 'movement-history' | 'harmful-terms';
  definition: string;
  context?: string;
  relatedTerms?: string[];
}
export function Terminology() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [expandedTerm, setExpandedTerm] = useState<string | null>(null);
  const terms: Term[] = [{
    word: 'Fat Liberation',
    category: 'core-concepts',
    definition: 'A political movement demanding the complete dismantling of systems, policies, and cultural norms that oppress, marginalize, and kill fat people.',
    context: "Fat liberation is not about individual self-acceptance or body positivity. It's about collective resistance and structural change.",
    relatedTerms: ['Anti-Fat Bias', 'Fatphobia', 'Size Justice']
  }, {
    word: 'Fatphobia',
    category: 'core-concepts',
    definition: 'Systemic oppression and discrimination against fat people, embedded in institutions, policies, and cultural norms.',
    context: "Fatphobia is not just individual prejudice—it's a system of oppression that operates at structural, institutional, and interpersonal levels.",
    relatedTerms: ['Anti-Fat Bias', 'Weight Stigma', 'Sizeism']
  }, {
    word: 'Anti-Fat Bias',
    category: 'core-concepts',
    definition: 'Negative attitudes, beliefs, and stereotypes about fat people that lead to discrimination and unequal treatment.',
    context: 'Anti-fat bias shows up in healthcare, employment, education, media representation, and everyday interactions.',
    relatedTerms: ['Fatphobia', 'Weight Stigma', 'Implicit Bias']
  }, {
    word: 'Health At Every Size (HAES)',
    category: 'core-concepts',
    definition: 'A weight-neutral approach to health that rejects the use of weight, size, or BMI as a measure of health. Developed by Lindo Bacon.',
    context: 'HAES principles include weight inclusivity, health enhancement, respectful care, eating for well-being, and life-enhancing movement.',
    relatedTerms: ['Weight-Neutral', 'Intuitive Eating', 'Body Autonomy']
  }, {
    word: 'Diet Culture',
    category: 'systems',
    definition: 'A system of beliefs that worships thinness, equates it with health and moral virtue, promotes weight loss as a means of attaining higher status, and demonizes certain foods while elevating others.',
    context: 'Diet culture is a $72 billion industry that profits from keeping people in a cycle of restriction and shame.',
    relatedTerms: ['Wellness Culture', 'Healthism', 'Thin Ideal']
  }, {
    word: 'Healthism',
    category: 'systems',
    definition: 'The belief that health is primarily a matter of individual choice and that health equals moral worth.',
    context: 'Healthism ignores social determinants of health and is used to justify discrimination against fat, disabled, and chronically ill people.',
    relatedTerms: ['Ableism', 'Moralization of Health', 'Wellness Culture']
  }, {
    word: 'BMI (Body Mass Index)',
    category: 'systems',
    definition: 'A flawed metric created in the 1800s by a Belgian mathematician (not a doctor) to study population trends, now misused as an individual health diagnostic.',
    context: 'BMI was developed using data from white European men and has racist and eugenicist origins. It does not measure health, body composition, or individual risk.',
    relatedTerms: ['Medicalization of Weight', 'Weight Science', 'Eugenics']
  }, {
    word: 'Weight Stigma',
    category: 'systems',
    definition: 'Social devaluation and denigration of individuals because of their body weight, leading to prejudice, negative stereotypes, and discrimination.',
    context: 'Weight stigma in healthcare leads to delayed diagnoses, inadequate treatment, and avoidance of medical care.',
    relatedTerms: ['Medical Fatphobia', 'Implicit Bias', 'Structural Discrimination']
  }, {
    word: 'The Fat Underground',
    category: 'movement-history',
    definition: 'A radical fat feminist group formed in Los Angeles in 1973, considered the birthplace of the Fat Liberation movement.',
    context: "The Fat Underground published the 'Fat Liberation Manifesto' and connected fat oppression to capitalism, patriarchy, and the medical-industrial complex.",
    relatedTerms: ['Fat Acceptance Movement', 'Radical Feminism', 'Fat Liberation Manifesto']
  }, {
    word: 'Fat Acceptance Movement',
    category: 'movement-history',
    definition: 'A social movement beginning in the 1960s advocating for the acceptance of fat people and challenging weight-based discrimination.',
    context: 'Key organizations include NAAFA (National Association to Advance Fat Acceptance), founded in 1969.',
    relatedTerms: ['The Fat Underground', 'Size Acceptance', 'Fat Rights']
  }, {
    word: 'Intersectionality',
    category: 'core-concepts',
    definition: 'A framework for understanding how multiple systems of oppression (racism, sexism, ableism, fatphobia, etc.) intersect and compound.',
    context: 'Coined by Kimberlé Crenshaw. In fat liberation, this means centering fat Black, Indigenous, disabled, trans, and poor people who face multiple, overlapping oppressions.',
    relatedTerms: ['Compounding Oppression', 'Multiple Marginalization', 'Structural Inequality']
  }, {
    word: 'Thin Privilege',
    category: 'core-concepts',
    definition: 'Unearned advantages that thin people receive in a fatphobic society, often invisible to those who have it.',
    context: 'Examples include: fitting in public seating, not being blamed for health issues, seeing yourself represented positively in media, not being discriminated against in employment.',
    relatedTerms: ['Privilege', 'Systemic Advantage', 'Structural Inequality']
  }, {
    word: 'Concern Trolling',
    category: 'harmful-terms',
    definition: "Disguising fatphobia as concern for someone's health. Often starts with 'I'm just worried about your health...'",
    context: "Concern trolling is a form of concern policing that assumes fat people don't know their own bodies and need unsolicited advice.",
    relatedTerms: ['Health Policing', 'Concern Policing', 'Unsolicited Advice']
  }, {
    word: 'Good Fatty / Bad Fatty',
    category: 'harmful-terms',
    definition: "A false dichotomy that divides fat people into 'acceptable' (those who exercise, eat 'healthy,' try to lose weight) and 'unacceptable' (those who don't).",
    context: "This framework upholds respectability politics and suggests some fat people deserve rights while others don't.",
    relatedTerms: ['Respectability Politics', 'Conditional Acceptance', 'Hierarchy of Bodies']
  }, {
    word: 'Body Positivity',
    category: 'harmful-terms',
    definition: 'Originally a radical movement, now co-opted by thin people and corporations to sell products while avoiding systemic critique.',
    context: "Body positivity has been watered down to 'all bodies are beautiful' without addressing power, oppression, or material conditions.",
    relatedTerms: ['Co-optation', 'Depoliticization', 'Corporate Feminism']
  }, {
    word: 'Smallfat / Superfat / Infinifat',
    category: 'core-concepts',
    definition: 'Terms describing different sizes within the fat community. Smallfat: sizes 14-18; Midfat: 18-24; Superfat: 24-32; Infinifat: 32+',
    context: 'These terms acknowledge that fat people experience different levels of oppression based on size. Smallfats have more access and privilege than superfats and infinifats.',
    relatedTerms: ['Size Hierarchy', 'Access', 'Privilege Within Fatness']
  }, {
    word: 'Medical Gaslighting',
    category: 'systems',
    definition: 'When healthcare providers dismiss, minimize, or misattribute symptoms to weight, refusing to investigate or treat actual health issues.',
    context: "Fat people are routinely told to 'just lose weight' instead of receiving proper diagnosis and treatment, leading to delayed care and preventable deaths.",
    relatedTerms: ['Medical Fatphobia', 'Diagnostic Overshadowing', 'Healthcare Discrimination']
  }, {
    word: 'Weight Cycling (Yo-Yo Dieting)',
    category: 'systems',
    definition: 'The pattern of losing and regaining weight repeatedly, which research shows is more harmful to health than remaining at a stable higher weight.',
    context: '95-98% of diets fail long-term. Weight cycling is associated with increased cardiovascular risk, inflammation, and psychological distress.',
    relatedTerms: ['Diet Failure', 'Set Point Theory', 'Metabolic Adaptation']
  }, {
    word: 'Set Point Theory',
    category: 'core-concepts',
    definition: "The theory that each person's body has a natural weight range it defends through metabolic and hormonal regulation.",
    context: 'Your body fights against sustained weight loss by slowing metabolism, increasing hunger hormones, and decreasing satiety signals.',
    relatedTerms: ['Weight Regulation', 'Homeostasis', 'Metabolic Adaptation']
  }, {
    word: 'Intuitive Eating',
    category: 'core-concepts',
    definition: 'An evidence-based approach to eating developed by Evelyn Tribole and Elyse Resch that rejects diet culture and honors internal hunger/fullness cues.',
    context: "Intuitive eating is not a diet or weight loss plan. It's about healing your relationship with food and trusting your body.",
    relatedTerms: ['Anti-Diet', 'Body Trust', 'Food Freedom']
  }];
  const categories = [{
    id: 'all',
    label: 'All Terms',
    color: 'bg-brand-magenta'
  }, {
    id: 'core-concepts',
    label: 'Core Concepts',
    color: 'bg-brand-magenta'
  }, {
    id: 'systems',
    label: 'Systems & Structures',
    color: 'bg-brand-cyan'
  }, {
    id: 'movement-history',
    label: 'Movement History',
    color: 'bg-brand-orange'
  }, {
    id: 'harmful-terms',
    label: 'Harmful Terms to Avoid',
    color: 'bg-gray-600'
  }];
  const filteredTerms = useMemo(() => {
    let filtered = terms;
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(term => term.category === selectedCategory);
    }
    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(term => term.word.toLowerCase().includes(searchQuery.toLowerCase()) || term.definition.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    // Sort alphabetically
    return filtered.sort((a, b) => a.word.localeCompare(b.word));
  }, [searchQuery, selectedCategory]);
  const getCategoryColor = (category: string) => {
    const colors = {
      'core-concepts': 'border-brand-magenta',
      systems: 'border-brand-cyan',
      'movement-history': 'border-brand-orange',
      'harmful-terms': 'border-gray-600'
    };
    return colors[category as keyof typeof colors];
  };
  const getCategoryBg = (category: string) => {
    const colors = {
      'core-concepts': 'bg-brand-magenta/5',
      systems: 'bg-brand-cyan/5',
      'movement-history': 'bg-brand-orange/5',
      'harmful-terms': 'bg-gray-100'
    };
    return colors[category as keyof typeof colors];
  };
  return <div className="min-h-screen bg-brand-cream" data-id="element-928">
      <HeroSection title="Fat Liberation Terminology" subtitle="A living glossary of key terms, concepts, and language in the movement." tag="Fat Liberation & Justice" bgColor="magenta" data-id="element-929" />

      {/* Search & Filter Bar */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto -mt-8 bg-white relative z-10 shadow-lg border-2 border-brand-black" data-id="element-930">
        <div className="space-y-6" data-id="element-931">
          {/* Search */}
          <div className="relative" data-id="element-932">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-933" />
            <input type="text" placeholder="Search terms..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 focus:border-brand-magenta focus:outline-none text-lg" data-id="element-934" />
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-3 flex-wrap" data-id="element-935">
            <div className="flex items-center gap-2 text-sm font-bold uppercase text-gray-600" data-id="element-936">
              <Filter className="h-4 w-4" data-id="element-937" />
              <span data-id="element-938">Filter:</span>
            </div>
            {categories.map(category => <button key={category.id} onClick={() => setSelectedCategory(category.id)} className={`px-4 py-2 text-xs font-bold uppercase tracking-wide transition-all border-2 ${selectedCategory === category.id ? `${category.color} text-white border-transparent` : 'bg-white text-gray-600 border-gray-200 hover:border-brand-magenta'}`} data-id="element-939">
                {category.label}
              </button>)}
          </div>

          {/* Results Count */}
          <div className="text-sm text-gray-500" data-id="element-940">
            Showing {filteredTerms.length}{' '}
            {filteredTerms.length === 1 ? 'term' : 'terms'}
          </div>
        </div>
      </section>

      {/* Glossary Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-941">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-942">
          <AnimatePresence mode="popLayout" data-id="element-943">
            {filteredTerms.map((term, index) => <motion.div key={term.word} layout initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} transition={{
            duration: 0.2,
            delay: index * 0.02
          }} className={`border-l-4 ${getCategoryColor(term.category)} ${getCategoryBg(term.category)} bg-white p-6 cursor-pointer hover:shadow-lg transition-shadow`} onClick={() => setExpandedTerm(expandedTerm === term.word ? null : term.word)} data-id="element-944">
                <div className="flex items-start justify-between mb-3" data-id="element-945">
                  <h3 className="text-xl font-black uppercase text-brand-black" data-id="element-946">
                    {term.word}
                  </h3>
                  <BookOpen className={`h-5 w-5 flex-shrink-0 ml-2 transition-transform ${expandedTerm === term.word ? 'rotate-180' : ''}`} data-id="element-947" />
                </div>

                <p className="text-gray-700 leading-relaxed mb-3" data-id="element-948">
                  {term.definition}
                </p>

                <AnimatePresence data-id="element-949">
                  {expandedTerm === term.word && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.2
              }} className="border-t-2 border-gray-100 pt-4 mt-4 space-y-4" data-id="element-950">
                      {term.context && <div data-id="element-951">
                          <h4 className="text-xs font-bold uppercase text-gray-500 mb-2" data-id="element-952">
                            Context:
                          </h4>
                          <p className="text-sm text-gray-600 leading-relaxed italic" data-id="element-953">
                            {term.context}
                          </p>
                        </div>}

                      {term.relatedTerms && term.relatedTerms.length > 0 && <div data-id="element-954">
                          <h4 className="text-xs font-bold uppercase text-gray-500 mb-2" data-id="element-955">
                            Related Terms:
                          </h4>
                          <div className="flex flex-wrap gap-2" data-id="element-956">
                            {term.relatedTerms.map(related => <span key={related} className="text-xs bg-brand-magenta/10 text-brand-magenta px-3 py-1 border border-brand-magenta/20 cursor-pointer hover:bg-brand-magenta/20 transition-colors" onClick={e => {
                      e.stopPropagation();
                      setSearchQuery(related);
                      setExpandedTerm(null);
                      window.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                      });
                    }} data-id="element-957">
                                {related}
                              </span>)}
                          </div>
                        </div>}
                    </motion.div>}
                </AnimatePresence>
              </motion.div>)}
          </AnimatePresence>
        </div>

        {filteredTerms.length === 0 && <div className="text-center py-16" data-id="element-958">
            <p className="text-xl text-gray-500 mb-4" data-id="element-959">
              No terms found matching "{searchQuery}"
            </p>
            <button onClick={() => {
          setSearchQuery('');
          setSelectedCategory('all');
        }} className="text-brand-magenta font-bold hover:underline" data-id="element-960">
              Clear filters
            </button>
          </div>}
      </section>

      {/* Note */}
      <section className="py-16 bg-brand-magenta/10" data-id="element-961">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-962">
          <div className="bg-white border-2 border-brand-black p-8" data-id="element-963">
            <h3 className="text-xl font-black uppercase mb-4 text-brand-black" data-id="element-964">
              A Living Document
            </h3>
            <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-965">
              Language in social justice movements evolves. This glossary
              reflects current usage in fat liberation spaces, but it's not
              exhaustive or final. We update it regularly based on community
              feedback and emerging discourse.
            </p>
            <p className="text-sm text-gray-600" data-id="element-966">
              <strong data-id="element-967">Note:</strong> Some terms in the "Harmful Terms to Avoid"
              category are included to help people understand why they're
              problematic, not to endorse their use.
            </p>
          </div>
        </div>
      </section>
    </div>;
}